import React from "react";
import {
  AlertTriangle,
  ChevronRight,
  Clock,
  Download,
  FileText,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { batchCsvUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

const relative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const absolute = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const HistoryPanel = ({
  items = [],
  loading,
  q,
  setQ,
  onRefresh,
  onLoad,
  onOpenDetail,
  onDelete,
  activeId,
}) => (
  <div className="vde-card flex h-full flex-col overflow-hidden" data-testid="history-panel">
    <div className="border-b border-border px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" aria-hidden="true" />
          <h2 className="text-sm font-semibold">Extraction history</h2>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onRefresh}
          aria-label="Refresh history"
          data-testid="history-refresh-button"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} aria-hidden="true" />
        </Button>
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        Every email, file and extracted row is kept here
      </p>
      <div className="relative mt-2.5">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search company, PAN, A/c, file…"
          className="h-9 bg-card pl-8 text-xs"
          data-testid="history-search-input"
        />
      </div>
    </div>

    <div className="flex-1 overflow-y-auto px-3 py-3" data-testid="history-list">
      {loading && !items.length ? (
        <div className="flex items-center justify-center gap-2 py-10 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Loading history…
        </div>
      ) : null}

      {!loading && !items.length ? (
        <div
          className="rounded-lg border border-dashed border-border bg-secondary/50 px-3 py-8 text-center"
          data-testid="history-empty"
        >
          <p className="text-xs font-medium text-foreground">No saved batches yet</p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Your first extraction will appear here automatically.
          </p>
        </div>
      ) : null}

      <ul className="space-y-2">
        {items.map((b) => (
          <li
            key={b.id}
            className={cn(
              "group rounded-lg border bg-card p-3 transition-colors",
              activeId === b.id
                ? "border-primary bg-primary/[0.06]"
                : "border-border hover:bg-secondary"
            )}
            data-testid="history-batch-item"
          >
            <button
              type="button"
              className="flex w-full items-start gap-2 text-left"
              onClick={() => onOpenDetail(b.id)}
              data-testid={`history-detail-button-${b.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-xs font-semibold text-foreground">
                    {b.vendor_names?.length ? b.vendor_names[0] : "No vendor found"}
                  </span>
                  {b.vendor_count > 1 ? (
                    <span className="shrink-0 rounded-full bg-secondary px-1.5 text-[10px] font-semibold">
                      +{b.vendor_count - 1}
                    </span>
                  ) : null}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-muted-foreground">
                  <span title={absolute(b.created_at)}>{relative(b.created_at)}</span>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1">
                    {b.file_count ? (
                      <>
                        <FileText className="h-2.5 w-2.5" aria-hidden="true" />
                        {b.file_count}
                      </>
                    ) : null}
                    {b.has_email ? <Mail className="ml-1 h-2.5 w-2.5" aria-hidden="true" /> : null}
                  </span>
                  <span>·</span>
                  <span className="vde-code">{b.vendor_count} rows</span>
                  {b.invalid_count ? (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-danger-surface px-1.5 font-semibold text-danger-text">
                      <AlertTriangle className="h-2.5 w-2.5" aria-hidden="true" />
                      {b.invalid_count}
                    </span>
                  ) : null}
                  {b.status === "failed" ? (
                    <span className="rounded-full bg-danger-surface px-1.5 font-semibold text-danger-text">
                      failed
                    </span>
                  ) : null}
                </div>
              </div>
              <ChevronRight
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                aria-hidden="true"
              />
            </button>

            <div className="mt-2 flex items-center gap-1.5">
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1 bg-card px-2 text-[11px]"
                onClick={() => onLoad(b.id)}
                data-testid={`history-restore-batch-button-${b.id}`}
              >
                <Upload className="h-3 w-3" aria-hidden="true" /> Load
              </Button>
              <a
                href={batchCsvUrl(b.id)}
                className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 text-[11px] font-medium transition-colors hover:bg-secondary"
                data-testid={`history-csv-button-${b.id}`}
              >
                <Download className="h-3 w-3" aria-hidden="true" /> CSV
              </a>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto h-7 w-7 p-0 text-muted-foreground hover:bg-danger-surface hover:text-danger-text"
                    aria-label="Delete batch"
                    data-testid={`history-delete-batch-button-${b.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-card">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this batch?</AlertDialogTitle>
                    <AlertDialogDescription>
                      The stored email body, uploaded files and {b.vendor_count} extracted row
                      {b.vendor_count === 1 ? "" : "s"} will be permanently removed.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() => onDelete(b.id)}
                      data-testid="delete-confirm-button"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export default HistoryPanel;
